/**
 * Thin wrapper around `@microsoft/signalr.HubConnection` — mirrors the Dart
 * `SocketGameHub` in packages/amgcommon/lib/socketConnection.dart so the call
 * shape matches the dragontiger reference exactly.
 *
 * One instance per hub URL. Owns the connection lifecycle and provides a
 * `sendMessage` / `registerOnServerEvents` pair so callers don't deal with
 * SignalR types directly.
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { debugLog, error as logError } from '../util/logger';

export interface HubLifecycle {
  onClose?: (error: Error | undefined) => void;
  onReconnecting?: (error: Error | undefined) => void;
  onReconnected?: (connectionId: string | undefined) => void;
}

export class SocketGameHub {
  private readonly url: string;
  private readonly conn: HubConnection;

  constructor(url: string, lifecycle: HubLifecycle = {}) {
    this.url = url;

    this.conn = new HubConnectionBuilder()
      // `withCredentials: false` — the staging server returns
      // `Access-Control-Allow-Origin: *`, which the browser refuses to accept
      // alongside the SignalR client's default `withCredentials: true`. We
      // don't rely on cookies for auth (sessionId is passed in JoinGame),
      // so disabling credentials is safe and matches the server config.
      .withUrl(url, { withCredentials: false })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.conn.onclose((err) => {
      debugLog('[SocketGameHub] closed', err);
      lifecycle.onClose?.(err as Error | undefined);
    });
    this.conn.onreconnecting((err) => {
      debugLog('[SocketGameHub] reconnecting', err);
      lifecycle.onReconnecting?.(err as Error | undefined);
    });
    this.conn.onreconnected((connectionId) => {
      debugLog('[SocketGameHub] reconnected', connectionId);
      lifecycle.onReconnected?.(connectionId);
    });
  }

  /** Start the underlying connection. Invokes the callback on success. */
  async startConnection(onStarted: () => void): Promise<void> {
    debugLog('[SocketGameHub] starting', this.url);
    try {
      await this.conn.start();
      debugLog('[SocketGameHub] connected', this.url);
      onStarted();
    } catch (e) {
      logError('[SocketGameHub] start failed', this.url, e);
      throw e;
    }
  }

  async stopConnection(): Promise<void> {
    if (this.conn.state === HubConnectionState.Disconnected) return;
    try {
      await this.conn.stop();
      debugLog('[SocketGameHub] stopped');
    } catch (e) {
      logError('[SocketGameHub] stop failed', e);
    }
  }

  get state(): HubConnectionState {
    return this.conn.state;
  }

  /** Invoke a server-side method (matches Dart `sendMessage`). */
  async sendMessage(event: string, args: unknown[]): Promise<void> {
    if (this.conn.state !== HubConnectionState.Connected) {
      debugLog(`[SocketGameHub] sendMessage(${event}) skipped — state=${this.conn.state}`);
      return;
    }
    debugLog(`[SocketGameHub] → ${event}`, args);
    try {
      await this.conn.invoke(event, ...args);
    } catch (e) {
      logError(`[SocketGameHub] ${event} send failed`, e);
    }
  }

  /** Subscribe to a server-emitted event. SignalR passes positional args. */
  registerOnServerEvents(eventName: string, handler: (args: unknown[]) => void): void {
    this.conn.on(eventName, (...args: unknown[]) => {
      debugLog(`[SocketGameHub] ← ${eventName}`, args);
      handler(args);
    });
  }
}
